import React, { useCallback, useMemo, useState } from 'react'
import { Button, Checkbox, Divider, Popover, Typography } from 'antd'
import { DragOutlined, SettingOutlined } from '@ant-design/icons'

const { Text } = Typography

export type TableColumnOption = {
  key: string
  label: string
  defaultVisible?: boolean
}

type StoredColumnLayout = {
  visible: string[]
  order: string[]
  widths: Record<string, number>
}

type Props = {
  storageKey: string
  columns: TableColumnOption[]
  defaultWidths: Record<string, number>
}

const readLayout = (
  storageKey: string,
  columns: TableColumnOption[],
  defaultWidths: Record<string, number>,
): StoredColumnLayout => {
  const keys = columns.map((column) => column.key)
  const defaults: StoredColumnLayout = {
    visible: columns
      .filter((column) => column.defaultVisible !== false)
      .map((column) => column.key),
    order: keys,
    widths: defaultWidths,
  }

  try {
    const saved = localStorage.getItem(storageKey)
    if (!saved) return defaults

    const parsed = JSON.parse(saved) as Partial<StoredColumnLayout> | string[]
    if (Array.isArray(parsed)) {
      return {
        ...defaults,
        visible: keys.filter(
          (key) =>
            parsed.includes(key) ||
            key === 'transactionId' ||
            key === 'updatedAt',
        ),
      }
    }
    const savedOrder = Array.isArray(parsed.order) ? parsed.order : []
    const savedVisible = Array.isArray(parsed.visible) ? parsed.visible : keys
    const order = [
      ...savedOrder.filter((key) => keys.includes(key)),
      ...keys.filter((key) => !savedOrder.includes(key)),
    ]

    return {
      visible: keys.filter((key) => savedVisible.includes(key)),
      order,
      widths: { ...defaultWidths, ...(parsed.widths ?? {}) },
    }
  } catch {
    return defaults
  }
}

const saveLayout = (storageKey: string, layout: StoredColumnLayout) => {
  localStorage.setItem(storageKey, JSON.stringify(layout))
}

export const useTableColumnSettings = ({
  storageKey,
  columns,
  defaultWidths,
}: Props) => {
  const [layout, setLayout] = useState(() =>
    readLayout(storageKey, columns, defaultWidths),
  )

  const updateLayout = useCallback(
    (next: StoredColumnLayout) => {
      setLayout(next)
      saveLayout(storageKey, next)
    },
    [storageKey],
  )

  const toggle = useCallback(
    (key: string) => {
      const visible = layout.visible.includes(key)
        ? layout.visible.filter((item) => item !== key)
        : [...layout.visible, key]
      updateLayout({ ...layout, visible })
    },
    [layout, updateLayout],
  )

  const reorder = useCallback(
    (sourceKey: string, targetKey: string) => {
      if (sourceKey === targetKey) return
      const order = [...layout.order]
      const sourceIndex = order.indexOf(sourceKey)
      const targetIndex = order.indexOf(targetKey)
      if (sourceIndex < 0 || targetIndex < 0) return
      order.splice(sourceIndex, 1)
      order.splice(targetIndex, 0, sourceKey)
      updateLayout({ ...layout, order })
    },
    [layout, updateLayout],
  )

  const resizeStart = useCallback(
    (event: React.MouseEvent, key: string) => {
      event.preventDefault()
      event.stopPropagation()
      const startX = event.clientX
      const startWidth = layout.widths[key] ?? 120

      const handleMove = (moveEvent: MouseEvent) => {
        const width = Math.max(60, startWidth + moveEvent.clientX - startX)
        setLayout((current) => ({
          ...current,
          widths: { ...current.widths, [key]: width },
        }))
      }
      const handleUp = () => {
        setLayout((current) => {
          saveLayout(storageKey, current)
          return current
        })
        window.removeEventListener('mousemove', handleMove)
        window.removeEventListener('mouseup', handleUp)
      }

      window.addEventListener('mousemove', handleMove)
      window.addEventListener('mouseup', handleUp)
    },
    [layout.widths, storageKey],
  )

  const reset = useCallback(() => {
    updateLayout({
      visible: columns
        .filter((column) => column.defaultVisible !== false)
        .map((column) => column.key),
      order: columns.map((column) => column.key),
      widths: defaultWidths,
    })
  }, [columns, defaultWidths, updateLayout])

  const settings = useMemo(
    () => (
      <Popover
        trigger="click"
        placement="bottomRight"
        content={
          <div style={{ width: 230 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              Columns
            </Text>
            {layout.order.map((key) => {
              const column = columns.find((item) => item.key === key)
              if (!column) return null
              return (
                <div
                  key={key}
                  draggable
                  onDragStart={(event) =>
                    event.dataTransfer.setData('text/plain', key)
                  }
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => {
                    event.preventDefault()
                    reorder(event.dataTransfer.getData('text/plain'), key)
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '4px 0',
                    cursor: 'grab',
                  }}
                >
                  <DragOutlined style={{ color: '#999' }} />
                  <Checkbox
                    checked={layout.visible.includes(key)}
                    onChange={() => toggle(key)}
                  >
                    <span style={{ fontSize: 12 }}>{column.label}</span>
                  </Checkbox>
                </div>
              )
            })}
            <Divider style={{ margin: '8px 0' }} />
            <Button size="small" type="text" onClick={reset}>
              Reset to default
            </Button>
          </div>
        }
      >
        <Button size="small" icon={<SettingOutlined />}>
          Columns
        </Button>
      </Popover>
    ),
    [columns, layout.order, layout.visible, reorder, reset, toggle],
  )

  const apply = useCallback(
    <T,>(tableColumns: T[]): T[] => {
      const orderIndex = new Map(layout.order.map((key, index) => [key, index]))
      const source = tableColumns as Array<{
        key?: React.Key
        dataIndex?: React.Key | React.Key[]
        title?: React.ReactNode
        width?: number
      }>
      return source
        .filter((column) => {
          const key = String(column.key ?? column.dataIndex ?? column.title)
          return layout.visible.includes(key)
        })
        .sort((a, b) => {
          const aKey = String(a.key ?? a.dataIndex ?? a.title)
          const bKey = String(b.key ?? b.dataIndex ?? b.title)
          return (
            (orderIndex.get(aKey) ?? Number.MAX_SAFE_INTEGER) -
            (orderIndex.get(bKey) ?? Number.MAX_SAFE_INTEGER)
          )
        })
        .map((column) => {
          const key = String(column.key ?? column.dataIndex ?? column.title)
          return {
            ...column,
            width: layout.widths[key] ?? (column as any).width,
          }
        }) as T[]
    },
    [layout],
  )

  return { settings, apply, resizeStart, layout }
}

export const resizableTitle = (
  title: React.ReactNode,
  key: string,
  resizeStart: (event: React.MouseEvent, key: string) => void,
) => (
  <div style={{ position: 'relative', paddingRight: 8 }}>
    {title}
    <span
      onMouseDown={(event) => resizeStart(event, key)}
      style={{
        position: 'absolute',
        top: -8,
        right: -8,
        width: 8,
        height: 32,
        cursor: 'col-resize',
      }}
    />
  </div>
)
